import { createMemo, createSignal, For, Show } from 'solid-js';
import { useTranslation } from '../hooks/useTranslation';
import Text from './Text';
import type { PlayerListDto, PlayerListSummaryDto } from '../api';
import styles from './PlayerListAttachPicker.module.css';

interface PlayerListAttachPickerProps {
    // Every player list the current user can see (owned + shared-with-me), the attach candidates.
    available: readonly PlayerListDto[];
    // Lists already attached to this whitelist/ban/access context, with their live member count.
    attached: readonly PlayerListSummaryDto[];
    onAttach: (playerListId: string) => void | Promise<void>;
    onDetach: (playerListId: string) => void | Promise<void>;
    disabled?: boolean;
    placeholderKey?: string;
    emptyKey?: string;
}

// Chip row + live search dropdown for attaching reusable PlayerLists to a PlotACL whitelist, ban
// list, or plot access list. Modeled on ItemIdentifierPicker's chip + onMouseDown-before-onBlur
// dropdown pattern; the candidates come from api.playerList.list instead of the item catalog.
export default function PlayerListAttachPicker(props: PlayerListAttachPickerProps) {
    const { t } = useTranslation();
    const [query, setQuery] = createSignal('');
    const [focused, setFocused] = createSignal(false);

    const attachedIds = createMemo(() => new Set(props.attached.map((l) => l.id)));

    const suggestions = createMemo(() => {
        const q = query().trim().toLowerCase();
        const ids = attachedIds();
        return props.available
            .filter((l) => !ids.has(l.id) && (q.length === 0 || l.name.toLowerCase().includes(q)))
            .slice(0, 10);
    });

    const attach = (id: string) => {
        void props.onAttach(id);
        setQuery('');
    };

    return (
        <div class={styles.picker}>
            <Show when={props.attached.length > 0}>
                <div class={styles.chips}>
                    <For each={props.attached}>
                        {(list) => (
                            <div class={styles.chip}>
                                <span class={styles.chipName}>{list.name}</span>
                                <span class={styles.chipCount}>{list.memberCount}</span>
                                <button type="button" class={styles.chipRemove} disabled={props.disabled}
                                    title={t('REMOVE')} onClick={() => void props.onDetach(list.id)}>
                                    <i class="fa-solid fa-xmark" />
                                </button>
                            </div>
                        )}
                    </For>
                </div>
            </Show>

            <div class={styles.searchWrap}>
                <i class={`fa-solid fa-list-check ${styles.searchIcon}`} />
                <input
                    type="text"
                    class={styles.searchInput}
                    value={query()}
                    onInput={(e) => setQuery(e.currentTarget.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setTimeout(() => setFocused(false), 120)}
                    placeholder={t(props.placeholderKey ?? 'PLOTACL_ATTACH_LIST_PLACEHOLDER')}
                    disabled={props.disabled}
                />
            </div>

            <Show when={focused()}>
                <div class={styles.dropdown}>
                    <Show when={suggestions().length > 0} fallback={
                        <div class={styles.empty}><Text val={props.emptyKey ?? 'PLOTACL_ATTACH_LIST_EMPTY'} /></div>
                    }>
                        <For each={suggestions()}>
                            {(list) => (
                                <button type="button" class={styles.option}
                                    onMouseDown={(e) => { e.preventDefault(); attach(list.id); }}>
                                    <span class={styles.optionName}>{list.name}</span>
                                    <span class={styles.optionCount}>{t('PLAYERLIST_MEMBER_COUNT', list.memberCount)}</span>
                                </button>
                            )}
                        </For>
                    </Show>
                </div>
            </Show>
        </div>
    );
}
